import dayjs from 'dayjs'
import { Amenity, CompactCruise, CompactCruiseSortBy } from '../../bewotec/openapi'
import { DEFAULT_DATE_FORMAT_WITH_WEEKDAY, ISO_DATE_FORMAT, SORT_KEYS } from '../../general/helper'
import { FieldValue } from '../filter/interface'
import { getValidDateFormat } from '../filter/validate'
import { ICruise, IFlightAvailabilities, IPriceModelCabinCategoryPricings } from './interfaces'

/* eslint-disable no-bitwise */

export const preprocessCatalog = (cruises: CompactCruise[]): ICruise[] => {
  const mappedCruises: ICruise[] = new Array(cruises.length)

  for (let i = 0; i < cruises.length; ++i) {
    const cruise = cruises[i]

    //
    // Route
    //
    const route = cruise.itinerary
    const routeName = route.name
    let firstPortDE = false
    let lastPortDE = false
    const portIdCount = route.segments?.length

    if (!!portIdCount) {
      const firstPort = route.segments?.at(0)
      const lastPort = route.segments?.at(-1)
      firstPortDE = firstPort?.port?.countryCode === 'DE'
      lastPortDE = lastPort?.port?.countryCode === 'DE'
    }

    //
    // Departure and Arrival
    //
    const abfahrtDe = dayjs(cruise.departureDate).format(DEFAULT_DATE_FORMAT_WITH_WEEKDAY)

    const arrivalDate = dayjs(cruise.departureDate).add(cruise.duration, 'day').format(ISO_DATE_FORMAT)
    const ankunftDe = dayjs(arrivalDate).format(DEFAULT_DATE_FORMAT_WITH_WEEKDAY)

    //
    // Campaigns
    //
    const campaigns: string[] = []

    cruise.offers.priceModelOffers.map(offer => {
      if (!!offer.campaigns?.length) {
        return offer.campaigns.map(campaign => {
          if (!!campaign.name && !campaigns.includes(campaign.name)) {
            campaigns.push(campaign.name)
          }
        })
      }
    })

    //
    // Flight Availabilities
    //
    const flightAvailabilities: IFlightAvailabilities = {}

    if (!!cruise.gateways?.length) {
      for (const gateway of cruise.gateways) {
        if (!!gateway) {
          flightAvailabilities[gateway.code] = { inbound: gateway.inbound, outbound: gateway.outbound }
        }
      }
    }

    const mappedCruise: ICruise = {
      routeName,
      duration: cruise.duration,
      firstPortDE,
      lastPortDE,
      id: cruise.cruiseId,
      shipId: cruise.shipCode,
      hasFlexFlight: cruise.offers.flexFlight,
      departure: abfahrtDe,
      arrival: ankunftDe,
      cruiseAttributes: cruise.cruiseAttributes ?? [],
      flightAvailabilities,
      cabinCategoryPricings: getCabinCategoryPricings(cruise),
      flightPricings: getBestFlightPricings(cruise),
      campaigns,
      amenities: getAmenities(cruise),
    }

    mappedCruises[i] = mappedCruise
  }

  return mappedCruises
}

const getCabinCategoryPricings = (cruise: CompactCruise): IPriceModelCabinCategoryPricings => {
  const cabinCategoryPricings: IPriceModelCabinCategoryPricings = {}

  for (const offer of cruise.offers.cabinCategoryOffers) {
    if (!!offer && !cabinCategoryPricings[offer.priceModelCode]) {
      cabinCategoryPricings[offer.priceModelCode] = {
        pricings: {
          [offer.cabinCategoryGroup.mainCode]: offer.pricing.amount ?? 0,
        },
      }
    } else if (!!offer && cabinCategoryPricings[offer.priceModelCode]) {
      cabinCategoryPricings[offer.priceModelCode].pricings[offer.cabinCategoryGroup.mainCode] =
        offer.pricing.amount ?? 0
    }
  }

  for (const offer of cruise.offers.priceModelOffers) {
    if (!!offer?.code && !!cabinCategoryPricings[offer.code]) {
      cabinCategoryPricings[offer.code].isMandatoryFlight = offer.mandatoryFlight ?? false
    }
  }

  return cabinCategoryPricings
}

const getBestFlightPricings = (cruise: CompactCruise) => {
  const flightPricings: IPriceModelCabinCategoryPricings = {}

  if (!cruise.offers.bestPriceflightOffer) return flightPricings

  for (const offer of cruise.offers.cabinCategoryOffers) {
    if (!!offer && !flightPricings[offer.priceModelCode]) {
      flightPricings[offer.priceModelCode] = {
        pricings: {
          [offer.cabinCategoryGroup.mainCode]: cruise.offers.bestPriceflightOffer.pricings.reduce(
            (accumulator, pricing) => accumulator + (pricing.amount ?? 0),
            0
          ),
        },
      }
    } else if (!!offer && !!flightPricings[offer.priceModelCode]) {
      flightPricings[offer.priceModelCode].pricings[offer.cabinCategoryGroup.mainCode] =
        cruise.offers.bestPriceflightOffer.pricings.reduce(
          (accumulator, pricing) => accumulator + (pricing.amount ?? 0),
          0
        )
    }
  }

  return flightPricings
}

const getAmenities = (cruise: CompactCruise): Amenity[] => {
  const amenities = cruise.offers.cabinCategoryOffers
    .filter(offer => !!offer.amenities?.length)
    .flatMap(offer => {
      return offer.amenities ?? []
    })

  return !!amenities.length ? amenities : []
}

export const getMinDuration = (durations: number) => {
  const hasDuration1Until5 = !!(durations & 1)
  const hasDuration6Until9 = !!(durations & 2)
  const hasDuration10Until13 = !!(durations & 4)
  const hasDuration14Until21 = !!(durations & 8)
  const hasDuration22Plus = !!(durations & 16)

  if (!durations || hasDuration1Until5) {
    return 1
  }

  if (hasDuration6Until9) {
    return 6
  }

  if (hasDuration10Until13) {
    return 10
  }

  if (hasDuration14Until21) {
    return 14
  }

  if (hasDuration22Plus) {
    return 22
  }
}

export const getMaxDuration = (durations: number) => {
  const hasDuration1Until5 = !!(durations & 1)
  const hasDuration6Until9 = !!(durations & 2)
  const hasDuration10Until13 = !!(durations & 4)
  const hasDuration14Until21 = !!(durations & 8)
  const hasDuration22Plus = !!(durations & 16)

  if (!durations || hasDuration22Plus) {
    return 200
  }

  if (hasDuration14Until21) {
    return 21
  }

  if (hasDuration10Until13) {
    return 13
  }

  if (hasDuration6Until9) {
    return 9
  }

  if (hasDuration1Until5) {
    return 5
  }
}

export const getFormattedDate = (fieldValue: FieldValue, format?: string): string => {
  const validDateFormat = getValidDateFormat(fieldValue)
  const now = dayjs()

  if (!validDateFormat?.format) return ''

  let dateValue = dayjs(fieldValue as string, validDateFormat.format, true)

  while (dateValue.isBefore(now)) {
    dateValue = dateValue.add(1, 'year')
  }

  return dateValue.format(format ?? ISO_DATE_FORMAT)
}

export const getSortKey = (sortKey: string | undefined, isSortingByPriceModel: boolean): CompactCruiseSortBy => {
  if (isSortingByPriceModel) return CompactCruiseSortBy.PRICEMODEL

  switch (sortKey) {
    case SORT_KEYS.route:
      return CompactCruiseSortBy.ROUTENAME
    case SORT_KEYS.abfahrt:
    default:
      return CompactCruiseSortBy.DEPDATE
  }
}

// -- For Testing Purposes Only----------------------------------
export const exportForTesting = {
  getAmenities,
  getCabinCategoryPricings,
  getBestFlightPricings,
  getSortKey,
}
